import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { HandDetector } from "@tensorflow-models/hand-pose-detection";
import type { ObjectDetection } from "@tensorflow-models/coco-ssd";
import useInterviewDetections, {
  type InterviewDetectionStatus,
  type InterviewDetectionWarning,
} from "@/hooks/useInterviewDetections";

export type MobileCameraDetectionSnapshot = {
  warning: InterviewDetectionWarning;
  status: InterviewDetectionStatus;
  handsDetected: number;
  workspaceDetected: boolean;
  detectionRunning: boolean;
};

type MobileCameraOverlayProps = {
  stream: MediaStream | null;
  handDetector?: HandDetector | null;
  objectDetector?: ObjectDetection | null;
  enableDetections?: boolean;
  flipHorizontal?: boolean;
  drawVideo?: boolean;
  muted?: boolean;
  autoPlay?: boolean;
  playsInline?: boolean;
  containerClassName?: string;
  videoClassName?: string;
  canvasClassName?: string;
  style?: CSSProperties;
  videoStyle?: CSSProperties;
  canvasStyle?: CSSProperties;
  detectionError?: Error | null;
  onReadyChange?: (isReady: boolean) => void;
  onDetectionUpdate?: (snapshot: MobileCameraDetectionSnapshot) => void;
  onStreamChange?: (stream: MediaStream | null) => void;
  mode?: "check-setup" | "interview" | "enhanced";
};

const joinClassNames = (...classes: Array<string | undefined | null>) =>
  classes.filter(Boolean).join(" ");

const MobileCameraOverlay: React.FC<MobileCameraOverlayProps> = ({
  stream,
  handDetector = null,
  objectDetector = null,
  enableDetections = true,
  flipHorizontal = false,
  drawVideo = false,
  muted = true,
  autoPlay = true,
  playsInline = true,
  containerClassName,
  videoClassName,
  canvasClassName,
  style,
  videoStyle,
  canvasStyle,
  detectionError = null,
  onReadyChange,
  onDetectionUpdate,
  onStreamChange,
  mode = "check-setup",
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isReady, setIsReady] = useState(false);

  const emitReadyChange = useCallback(
    (ready: boolean) => {
      setIsReady(ready);
      onReadyChange?.(ready);
    },
    [onReadyChange],
  );

  useEffect(() => {
    const videoElement = videoRef.current;
    const canvasElement = canvasRef.current;

    if (!videoElement || !canvasElement) {
      return;
    }

    const handleLoadedMetadata = () => {
      if (!videoElement || !canvasElement) {
        return;
      }

      const { videoWidth, videoHeight } = videoElement;
      if (videoWidth && videoHeight) {
        canvasElement.width = videoWidth;
        canvasElement.height = videoHeight;
        emitReadyChange(true);
      }
    };

    videoElement.onloadedmetadata = handleLoadedMetadata;

    if (stream) {
      if (videoElement.srcObject !== stream) {
        videoElement.srcObject = stream;
      }
      if (videoElement.readyState >= 1) {
        handleLoadedMetadata();
      }
      void videoElement.play().catch(() => undefined);
      onStreamChange?.(stream);
    } else {
      videoElement.srcObject = null;
      canvasElement.width = 0;
      canvasElement.height = 0;
      emitReadyChange(false);
      onStreamChange?.(null);
    }

    return () => {
      if (videoElement) {
        videoElement.onloadedmetadata = null;
      }
    };
  }, [stream, emitReadyChange, onStreamChange]);

  const isDetectionActive = useMemo(
    () => Boolean(stream && isReady && enableDetections && !detectionError),
    [stream, isReady, enableDetections, detectionError],
  );

  const { handInterval, workspaceInterval } = useMemo(() => {
    switch (mode) {
      case "interview":
        return {
          handInterval: 100,
          workspaceInterval: 10_000,
        };
      case "enhanced":
        return {
          handInterval: 100,
          workspaceInterval: 100,
        };
      case "check-setup":
      default:
        return {
          handInterval: 100,
          workspaceInterval: 1_000,
        };
    }
  }, [mode]);

  const detectionResult = useInterviewDetections({
    videoRef,
    canvasRef,
    handDetector,
    objectDetector,
    isActive: isDetectionActive,
    flipHorizontal,
    drawVideo,
    handDetectionIntervalMs: handInterval,
    workspaceDetectionIntervalMs: workspaceInterval,
  });

  useEffect(() => {
    onDetectionUpdate?.({
      warning: detectionResult.warning,
      status: detectionResult.status,
      handsDetected: detectionResult.handsDetected,
      workspaceDetected: detectionResult.workspaceDetected,
      detectionRunning: detectionResult.detectionRunning,
    });
  }, [
    detectionResult.detectionRunning,
    detectionResult.handsDetected,
    detectionResult.status,
    detectionResult.warning,
    detectionResult.workspaceDetected,
    onDetectionUpdate,
  ]);

  return (
    <div className={joinClassNames("relative", containerClassName)} style={style}>
      <video
        ref={videoRef}
        className={joinClassNames("h-full w-full object-contain", videoClassName)}
        style={videoStyle}
        autoPlay={autoPlay}
        playsInline={playsInline}
        muted={muted}
      />
      <canvas
        ref={canvasRef}
        className={joinClassNames("pointer-events-none absolute inset-0 h-full w-full", canvasClassName)}
        style={canvasStyle}
      />
    </div>
  );
};

export default MobileCameraOverlay;
